import { useEffect, useId, useMemo, useRef, useState, type MutableRefObject } from 'react';
import { Pause, Play, Sun, X } from 'lucide-react';
import type { ParcelFrameUpdate } from './ParcelDimensionsOverlay';
import { projectParcelRing, projectToScreen, projectWorldPath, sunAnglesToWorld, sunBeamBase, sunHorizonRing, type WorldPos } from '../utils/measureUtils';
import { formatMedicalLakeTime, medicalLakeDate, medicalLakeInstant, medicalLakeSunDay, medicalLakeSunPosition, sunPathTimeMarkers, sunlightAppearance } from '../utils/sunPathUtils';
import { useI18n } from '../i18n';

const DISPLAY_RADIUS = 24;

export default function SunPathOverlay({ origin, frameRef, onClose, onFitView }: {
  origin: WorldPos; frameRef: MutableRefObject<ParcelFrameUpdate | null>; onClose: () => void; onFitView: () => void;
}) {
  const { t, lang } = useI18n();
  const text = t.sunPath;
  const id = useId().replace(/:/g, '');
  const locale = lang === 'fr' ? 'fr-CA' : 'en-US';
  const [date, setDate] = useState(medicalLakeDate);
  const [minute, setMinute] = useState(720);
  const [playing, setPlaying] = useState(false);
  const day = useMemo(() => medicalLakeSunDay(date), [date]);
  const selectedMinute = day ? Math.max(day.startMinute, Math.min(day.endMinute, minute)) : 720;
  const instant = useMemo(() => medicalLakeInstant(date, selectedMinute), [date, selectedMinute]);
  const position = useMemo(() => medicalLakeSunPosition(instant), [instant]);
  const light = useMemo(() => sunlightAppearance(position.altitude), [position.altitude]);
  const path = useMemo(() => day?.samples.map(p => sunAnglesToWorld(p.azimuth, p.altitude, origin, DISPLAY_RADIUS)) ?? [], [day, origin]);
  const sun = useMemo(() => sunAnglesToWorld(position.azimuth, position.altitude, origin, DISPLAY_RADIUS), [position, origin]);
  const compass = useMemo(() => [0, 90, 180, 270].map(az => sunAnglesToWorld(az, 0, origin, DISPLAY_RADIUS)), [origin]);
  const horizon = useMemo(() => sunHorizonRing(origin, DISPLAY_RADIUS), [origin]);
  const beam = useMemo(() => [sun, ...sunBeamBase(position.azimuth, origin)], [sun, position.azimuth, origin]);
  const timeMarkers = useMemo(() => day ? sunPathTimeMarkers(date, day).map(marker => {
    const angles = medicalLakeSunPosition(marker.instant);
    return { ...marker, point: sunAnglesToWorld(angles.azimuth, angles.altitude, origin, DISPLAY_RADIUS) };
  }) : [], [date, day, origin]);
  const trail = useMemo(() => {
    if (!day) return [];
    const fraction = (instant.getTime() - day.sunrise.getTime()) / (day.sunset.getTime() - day.sunrise.getTime());
    return [...path.slice(0, Math.max(1, Math.floor(fraction * (path.length - 1)) + 1)), sun];
  }, [day, instant, path, sun]);
  const arcRef = useRef<SVGPathElement>(null);
  const haloRef = useRef<SVGPathElement>(null);
  const trailRef = useRef<SVGPathElement>(null);
  const horizonRef = useRef<SVGPathElement>(null);
  const beamRef = useRef<SVGPolygonElement>(null);
  const beamGradientRef = useRef<SVGLinearGradientElement>(null);
  const rayRef = useRef<SVGPathElement>(null);
  const sunRef = useRef<SVGGElement>(null);
  const clockRef = useRef<HTMLDivElement>(null);
  const tickRefs = useRef<(HTMLDivElement | null)[]>([]);
  const compassRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (!playing || !day) return;
    if (selectedMinute >= day.endMinute) {
      setPlaying(false);
      return;
    }
    const timer = window.setTimeout(() => setMinute(m => {
      const current = Math.max(day.startMinute, Math.min(day.endMinute, m));
      return Math.min(day.endMinute, current + 5);
    }), 200);
    return () => window.clearTimeout(timer);
  }, [playing, day, selectedMinute]);

  useEffect(() => {
    const update: ParcelFrameUpdate = (camera, canvas) => {
      const arc = projectWorldPath(path, camera, canvas);
      arcRef.current?.setAttribute('d', arc);
      haloRef.current?.setAttribute('d', arc);
      trailRef.current?.setAttribute('d', projectWorldPath(trail, camera, canvas));
      horizonRef.current?.setAttribute('d', projectWorldPath(horizon, camera, canvas));
      rayRef.current?.setAttribute('d', projectWorldPath([origin, sun], camera, canvas));
      const rect = canvas.getBoundingClientRect();
      const projectedSun = projectToScreen(sun, camera, canvas);
      const projectedOrigin = projectToScreen(origin, camera, canvas);
      const projectedBeam = projectParcelRing(beam, camera, canvas);
      if (beamRef.current) {
        beamRef.current.style.visibility = projectedBeam ? 'visible' : 'hidden';
        beamRef.current.setAttribute('points', projectedBeam?.map(p => `${p.x + rect.left},${p.y + rect.top}`).join(' ') ?? '');
      }
      const gradient = beamGradientRef.current;
      gradient?.setAttribute('x1', String(projectedSun.x + rect.left));
      gradient?.setAttribute('y1', String(projectedSun.y + rect.top));
      gradient?.setAttribute('x2', String(projectedOrigin.x + rect.left));
      gradient?.setAttribute('y2', String(projectedOrigin.y + rect.top));
      if (sunRef.current) {
        sunRef.current.style.visibility = projectedSun.visible ? 'visible' : 'hidden';
        sunRef.current.setAttribute('transform', `translate(${projectedSun.x + rect.left},${projectedSun.y + rect.top})`);
      }
      if (clockRef.current) {
        clockRef.current.style.visibility = projectedSun.visible ? 'visible' : 'hidden';
        clockRef.current.style.translate = `${projectedSun.x + rect.left}px ${projectedSun.y + rect.top}px`;
      }
      timeMarkers.forEach((marker, i) => {
        const el = tickRefs.current[i];
        const screen = projectToScreen(marker.point, camera, canvas);
        if (el) {
          el.style.visibility = screen.visible ? 'visible' : 'hidden';
          el.style.translate = `${screen.x + rect.left}px ${screen.y + rect.top}px`;
        }
      });
      compass.forEach((p, i) => {
        const el = compassRefs.current[i];
        const screen = projectToScreen(p, camera, canvas);
        if (el) {
          el.style.visibility = screen.visible ? 'visible' : 'hidden';
          el.style.translate = `${screen.x + rect.left}px ${screen.y + rect.top}px`;
        }
      });
    };
    frameRef.current = update;
    return () => { if (frameRef.current === update) frameRef.current = null; };
  }, [path, trail, horizon, beam, timeMarkers, sun, origin, compass, frameRef]);

  const changeDate = (value: string) => {
    if (!value) return;
    setPlaying(false); setDate(value); setMinute(720);
  };
  const controls = (
    <aside data-sun-controls className="fixed left-4 top-4 z-[1003] max-h-[calc(100vh-2rem)] w-56 overflow-y-auto rounded-xl border border-amber-100/30 border-l-2 border-l-amber-200/60 bg-gradient-to-br from-[#3a3021] to-[#29251e] text-white shadow-lg sm:w-60" aria-label={text.title}>
      <header className="flex items-center gap-2 border-b border-amber-100/15 bg-amber-100/5 px-3 py-2">
        <Sun size={15} className="text-amber-100/80" /><h2 className="flex-1 text-xs font-semibold text-amber-100/90">{text.title}</h2>
        <button type="button" onClick={onClose} aria-label={text.close} className="rounded p-1 text-white/50 hover:text-white"><X size={14} /></button>
      </header>
      <div className="space-y-2 px-3 py-2 text-[11px]">
        <label className="flex items-center justify-between gap-2 text-white/65">{text.date}
          <input type="date" value={date} min="2020-01-01" max="2100-12-31" onChange={e => changeDate(e.target.value)}
            className="w-36 rounded border border-white/15 bg-white/5 px-2 py-1 text-[11px] text-white/90" style={{ colorScheme: 'dark' }} />
        </label>
        <div className="flex gap-2">{[[text.today, medicalLakeDate()], [text.summer, `${date.slice(0,4)}-06-21`], [text.winter, `${date.slice(0,4)}-12-21`]].map(([label, value]) =>
          <button key={label} type="button" onClick={() => changeDate(value)} className="flex-1 rounded border border-white/10 px-2 py-1 text-white/65 hover:bg-white/10">{label}</button>)}</div>
        <button type="button" onClick={onFitView} className="text-[10px] text-amber-100/65 underline hover:text-amber-100">{text.fit}</button>
        {day ? <>
          <label className="block text-white/60">{text.localTime}
            <span className="mt-1 block text-sm font-medium text-amber-100/90">{formatMedicalLakeTime(instant, locale)}</span>
            <span className="mt-1 block text-[10px]" style={{ color: light.color }}>{light.level === 'low' ? text.lowSun : light.level === 'mid' ? text.midSun : text.highSun}</span>
            <input type="range" min={day.startMinute} max={day.endMinute} step="1" value={selectedMinute}
              aria-valuetext={formatMedicalLakeTime(instant, locale)}
              onChange={e => { setPlaying(false); setMinute(Number(e.target.value)); }} className="mt-2 w-full accent-amber-200/80" />
          </label>
          <div className="grid grid-cols-2 gap-3 text-white/55">
            <div>{text.sunrise}<div className="mt-0.5 text-white/85">{formatMedicalLakeTime(day.sunrise, locale)}</div></div>
            <div>{text.sunset}<div className="mt-0.5 text-white/85">{formatMedicalLakeTime(day.sunset, locale)}</div></div>
          </div>
          <div className="hidden">
            <div>{text.elevation}<span className="ml-1 text-white/85">{position.altitude.toFixed(0)}°</span></div>
            <div>{text.direction}<span className="ml-1 text-white/85">{position.azimuth.toFixed(0)}°</span></div>
          </div>
          <button type="button" onClick={() => {
            if (!playing && selectedMinute >= day.endMinute) setMinute(day.startMinute);
            setPlaying(v => !v);
          }} className="flex w-full items-center justify-center gap-2 rounded border border-amber-100/20 bg-amber-100/5 py-1.5 text-amber-100/85 hover:bg-amber-100/10">
            {playing ? <Pause size={12} /> : <Play size={12} />}{playing ? text.pause : text.play}
          </button>
        </> : <p>{text.unavailable}</p>}
        <p className="text-[10px] leading-relaxed text-white/45">{text.notice}</p>
      </div>
    </aside>
  );
  return <>
    <div className="pointer-events-none fixed inset-0 z-[1001]" aria-hidden="true">
      <svg className="absolute inset-0 h-full w-full">
        <defs>
          <linearGradient id={`${id}-arc`}><stop offset="0%" stopColor="#eab89d" /><stop offset="50%" stopColor="#fff0b5" /><stop offset="100%" stopColor="#e8b4a9" /></linearGradient>
          <radialGradient id={`${id}-sun`}><stop stopColor="#fffff4" /><stop offset="60%" stopColor={light.color} /><stop offset="100%" stopColor={light.color} /></radialGradient>
          <radialGradient id={`${id}-glow`}><stop stopColor={light.color} stopOpacity={light.glowOpacity} /><stop offset="100%" stopColor={light.color} stopOpacity="0" /></radialGradient>
          <linearGradient ref={beamGradientRef} id={`${id}-beam`} gradientUnits="userSpaceOnUse"><stop data-sun-beam-color stopColor={light.beamColor} stopOpacity={light.beamOpacity} /><stop offset="100%" stopColor={light.beamColor} stopOpacity={light.beamEndOpacity} /></linearGradient>
          <filter id={`${id}-soft`} x="-30%" y="-30%" width="160%" height="160%"><feGaussianBlur stdDeviation={light.beamBlur} /></filter>
        </defs>
        <path ref={horizonRef} fill="none" stroke="rgba(235,231,213,0.18)" strokeWidth="1" strokeDasharray="2 8" />
        <polygon ref={beamRef} fill={`url(#${id}-beam)`} filter={`url(#${id}-soft)`} />
        <path ref={rayRef} fill="none" stroke={light.color} opacity={light.rayOpacity} strokeWidth="1" />
        <path ref={haloRef} fill="none" stroke={`url(#${id}-arc)`} strokeWidth="9" opacity="0.09" strokeLinecap="round" />
        <path ref={arcRef} data-sun-arc fill="none" stroke={`url(#${id}-arc)`} strokeWidth="1.5" opacity="0.65" strokeLinecap="round" />
        <path ref={trailRef} fill="none" stroke={`url(#${id}-arc)`} strokeWidth="2.5" opacity="0.9" strokeLinecap="round" />
        <g ref={sunRef} style={{ visibility: 'hidden' }}>
          <circle r={light.glowRadius} fill={`url(#${id}-glow)`} />
          <circle r="24" fill={light.color} opacity="0.07" />
          <path d="M0 -20V-16M0 16V20M-20 0H-16M16 0H20M-14 -14L-11 -11M11 11L14 14M-14 14L-11 11M11 -11L14 -14" stroke={light.color} strokeWidth="1.5" strokeLinecap="round" opacity="0.8" />
          <circle r="10" fill={`url(#${id}-sun)`} stroke="rgba(255,255,255,0.75)" strokeWidth="0.8" style={{ filter: `drop-shadow(0 0 8px ${light.color})` }} />
        </g>
      </svg>
      {timeMarkers.map((marker, i) => <div key={`${date}-${i}`} ref={el => { tickRefs.current[i] = el; }}
        className="fixed left-0 top-0 flex flex-col items-center text-[9px] text-amber-50/65"
        style={{ visibility: 'hidden', transform: 'translate(-50%, -3px)',
          opacity: Math.abs(marker.instant.getTime() - instant.getTime()) < 20 * 60_000 ? 0 : 1 }}>
        <span className="h-1.5 w-1.5 rounded-full border border-amber-100/70 bg-[#111827]" />
        <span className="mt-2 whitespace-nowrap rounded border border-white/10 bg-[#111827]/80 px-2 py-1 backdrop-blur-sm">
          {marker.kind === 'sunrise' ? `${text.sunrise} · ` : marker.kind === 'sunset' ? `${text.sunset} · ` : ''}{formatMedicalLakeTime(marker.instant, locale, false)}
        </span>
      </div>)}
      <div ref={clockRef} className="fixed left-0 top-0 whitespace-nowrap rounded-full border border-amber-100/25 bg-[#111827]/90 px-3 py-1 text-[11px] font-medium text-amber-100 shadow-lg backdrop-blur-md"
        style={{ visibility: 'hidden', transform: 'translate(-50%, -52px)' }}>{formatMedicalLakeTime(instant, locale, false)}</div>
      {['N', 'E', 'S', text.west].map((label, i) => <div key={i} ref={el => { compassRefs.current[i] = el; }}
        className="fixed left-0 top-0 rounded border border-amber-100/15 bg-[#111827]/85 px-1.5 py-0.5 text-[10px] text-amber-100/70"
        style={{ visibility: 'hidden', transform: 'translate(-50%, -50%)' }}>{label}</div>)}
    </div>
    {controls}
  </>;
}
